import { Button, Form, Input, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { DownloadOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import moment from 'moment';
import style from './Login.module.scss';
import { AppState } from '../../store';
import ErrorMessage from '../../globals/Components/Error/ErrorMessage';
import { removeloginCookie } from '../../services/auth';
import validateMessages from '../../globals/Inputs/validateMessages';
import { CredentialsRequest } from '../../store/user/types';
import { forceRemoveAllCookies } from '../../services/cookies';
import { getRecoverPassword, postLogin } from '../../store/user/thunks';
import { getThemePreference, setThemePreference, ThemeMode } from '../../services/themePreference';

interface LoginProps {
    errorMessage?: any;
    postLogin?: any;
    getRecoverPassword?: any;
    showCheckInLink?: boolean;
}

const LOGO_URL = 'https://repositorysmilemed.blob.core.windows.net/images/logos/sinfondologotbc.png';

const Login: React.FC<LoginProps> = (props: LoginProps) => {
    const [step, setStep] = useState(0);
    const [isEmail, setEmail] = useState('');
    const [themeMode, setThemeMode] = useState<ThemeMode>(getThemePreference());

    moment.locale('es');
    removeloginCookie();

    const messageResponse = props.errorMessage?.message ? (
        <ErrorMessage>
            {props.errorMessage.message.map((item: string | null | undefined) => (
                <p key={item || 'message'}>{item}</p>
            ))}
        </ErrorMessage>
    ) : null;

    useEffect(() => {
        forceRemoveAllCookies();
    }, []);

    const handleThemeChange = (checked: boolean) => {
        const nextTheme = checked ? 'dark' : 'light';
        setThemeMode(nextTheme);
        setThemePreference(nextTheme);
    };

    const getRecoveredEmail = (response: any) => {
        if (!response) {
            return '';
        }

        if (typeof response === 'string') {
            return response;
        }

        return response.email || response.Email || response.userEmail || response.correo || '';
    };

    const StepLogin = () => {
        const onFinish = async (values: any) => {
            const userLoginState: CredentialsRequest = {
                username: values.username,
                password: values.password
            };

            await props.postLogin(userLoginState);
        };

        return (
            <Form layout="vertical" name="login" onFinish={onFinish} className={style["auth-form"]}>
                <div className={style["welcome-copy"]}>Bienvenido de vuelta</div>
                <h1 className={style["login-title"]}>Ingresa a tu portal</h1>
                <p className={style["login-subtitle"]}>Gestiona planes, contratos y facturacion en una experiencia clara.</p>

                <Form.Item
                    label="Identificacion"
                    name="username"
                    rules={[{ required: true, message: 'Por favor ingrese su identificacion' }]}
                >
                    <Input placeholder="1727828447" className={style["auth-input"]} />
                </Form.Item>

                <Form.Item
                    label="Contrasena"
                    name="password"
                    rules={[{ required: true, message: 'Por favor ingrese su contrasena' }]}
                >
                    <Input.Password placeholder="********" className={style["auth-input"]} />
                </Form.Item>

                <button type="button" onClick={() => setStep(1)} className={style["forgot-button"]}>
                    ¿Olvidaste tu contrasena?
                </button>

                {messageResponse}

                <Button className={style["button"]} htmlType="submit">
                    Ingresar
                </Button>

                {props.showCheckInLink && (
                    <Button
                        className={style["checkin-link-button"]}
                        type="primary"
                        onClick={() => {
                            window.open('https://portalsalud.sime.com.ec/sime-app-go', '_blank');
                        }}
                    >
                        App SimeGO
                        <DownloadOutlined />
                    </Button>
                )}

            </Form>
        );
    };

    const StepForgotPassword = () => {
        const [form] = Form.useForm();

        const onFinish = async (values: any) => {
            const response = await props.getRecoverPassword(values.identification);
            if (response) {
                setEmail(getRecoveredEmail(response));
                setStep(2);
            }
        };

        return (
            <Form
                name="recover-password"
                form={form}
                layout="vertical"
                onFinish={onFinish}
                className={style["auth-form"]}
            >
                <div className={style["welcome-copy"]}>Recuperacion de acceso</div>
                <h1 className={style["login-title"]}>Restablece tu contrasena</h1>
                <p className={style["login-subtitle"]}>Te enviaremos un enlace al correo asociado a tu cuenta.</p>

                <Form.Item
                    label="Identificacion"
                    name="identification"
                    rules={[{ required: true, message: validateMessages.emptyField }]}
                >
                    <Input placeholder="Ingresa tu cedula" className={style["auth-input"]} />
                </Form.Item>

                {messageResponse}

                <Button className={style["button"]} type="primary" htmlType="submit">
                    Recuperar contrasena
                </Button>

                <button type="button" onClick={() => setStep(0)} className={style["back-button"]}>
                    Regresar al inicio
                </button>
            </Form>
        );
    };

    const StepForgotPasswordSuccess = () => (
        <div className={style["success-state"]}>
            <img loading="lazy" src={LOGO_URL} alt="The Blue Coding" />
            <h1 className={style["login-title"]}>Correo enviado</h1>
            <p className={style["login-subtitle"]}>
                Se envio un correo electronico a {isEmail || 'tu cuenta'} para recuperar tu contrasena.
            </p>
            <Button className={style["button"]} onClick={() => setStep(0)}>
                Regresar
            </Button>
        </div>
    );

    return (
        <main className={style["login-container"]}>
            <section className={style["login-shell"]}>
                <aside className={style["visual-panel"]}>
                    <div className={style["metric-card"]}>
                        <span>Disponibilidad</span>
                        <strong>99%</strong>
                        <div className={style["metric-wave"]} />
                    </div>
                    <div className={style["access-badge"]}>
                        <strong>24/7</strong>
                        <span>Acceso</span>
                    </div>
                    <div className={style["feature-card"]}>
                        <div>
                            <span />
                            <span />
                            <span />
                        </div>
                        <section>
                            <strong>Portal The Blue Coding</strong>
                            <p>Clientes, pagos y renovaciones conectados en una sola experiencia.</p>
                        </section>
                    </div>
                    <div className={style["visual-copy"]}>
                        <h2>Tu portal, claro y ordenado.</h2>
                        <p>The Blue Coding ordena la gestion de clientes, pagos y renovaciones en una experiencia clara.</p>
                    </div>
                </aside>

                <div className={style["login-panel"]}>
                    <div className={style["theme-switcher"]}>
                        <SunOutlined />
                        <Switch
                            checked={themeMode === 'dark'}
                            onChange={handleThemeChange}
                            aria-label="Cambiar tema"
                        />
                        <MoonOutlined />
                    </div>
                    <img className={style["brand-logo"]} src={LOGO_URL} alt="The Blue Coding" />
                    {step === 1 ? <StepForgotPassword /> : step === 2 ? <StepForgotPasswordSuccess /> : <StepLogin />}
                </div>
            </section>
        </main>
    );
};

const mapStateToProps = (state: AppState) => ({
    errorMessage: state.system.messageResponse
});

export default connect(mapStateToProps, {
    postLogin,
    getRecoverPassword,
})(Login);
